/* ============================================================
   Deal Management — embedded shell.
   Renders the whole module INLINE inside the investor dashboard's
   "Deal Management" tab (see Advisor/ClientIndex/index.js), the same
   way Portfolio / InvestorMatch / AnyList render their panels.

   The module is a self-contained mini-app with its own router. React
   Router v6 forbids a <Router> nested inside the app's <BrowserRouter>,
   so the module is mounted into a SEPARATE React root (its own context
   tree) where its MemoryRouter is legal. The module has no Redux / MUI
   / app-context dependencies (verified) — it uses its own contexts plus
   the global axios/socket helpers — so isolation is safe.

   Result: inline in the tab, pipeline → deal → step navigation stays
   internal (browser URL does not change), slim nav, light theme only.
   ============================================================ */
'use strict';
import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';

import './dealManagement.css';
import { ToastProvider } from './components/Toast';
import { DealStateProvider } from './state';
import AppBar from './components/AppBar';
import Pipeline from './screens/Pipeline';
import Deal from './screens/Deal';
import Dashboard from './screens/Dashboard';
import Profile from './screens/Profile';
import Sandbox from './screens/Sandbox';

class DealErrorBoundary extends React.Component {
	constructor(props) { super(props); this.state = { error: null }; }
	static getDerivedStateFromError(error) { return { error }; }
	componentDidCatch(error, info) { console.error('[DealManagement] render error:', error, info); }
	render() {
		if (this.state.error) {
			return (
				<div className="dlg-dm" style={{ padding: '24px 28px' }}>
					<div className="card" style={{ maxWidth: 640 }}>
						<div className="card__label" style={{ marginBottom: 8, color: 'var(--dlg-red-ink)' }}>Deal Management failed to load</div>
						<div style={{ fontSize: 13.5, color: 'var(--dlg-ink-soft)', whiteSpace: 'pre-wrap' }}>{String(this.state.error && (this.state.error.stack || this.state.error.message || this.state.error))}</div>
					</div>
				</div>
			);
		}
		return this.props.children;
	}
}

// The isolated module app — its own MemoryRouter lives here, in a separate React root.
function DealApp() {
	return (
		<DealErrorBoundary>
			<MemoryRouter initialEntries={['/deal-management/pipeline']}>
				<div className="dlg-dm">
					<ToastProvider>
						<DealStateProvider>
							<AppBar embedded />
							<Routes>
								<Route path="/deal-management" element={<Navigate to="/deal-management/pipeline" replace />} />
								<Route path="/deal-management/pipeline" element={<Pipeline view="board" />} />
								<Route path="/deal-management/pipeline/list" element={<Pipeline view="list" />} />
								<Route path="/deal-management/dashboard" element={<Dashboard />} />
								<Route path="/deal-management/profile" element={<Profile />} />
								<Route path="/deal-management/sandbox" element={<Sandbox />} />
								<Route path="/deal-management/deal/:id/:step" element={<Deal />} />
								<Route path="/deal-management/deal/:id/:step/:tab" element={<Deal />} />
								<Route path="*" element={<Navigate to="/deal-management/pipeline" replace />} />
							</Routes>
						</DealStateProvider>
					</ToastProvider>
				</div>
			</MemoryRouter>
		</DealErrorBoundary>
	);
}

export default function EmbeddedDealManagement() {
	const hostRef = useRef(null);
	const rootRef = useRef(null);

	useEffect(() => {
		const host = hostRef.current;
		if (!host) return;
		const root = createRoot(host);
		rootRef.current = root;
		root.render(<DealApp />);
		// Defer unmount out of the parent's commit phase to avoid the React
		// "synchronously unmount a root while rendering" warning.
		return () => { rootRef.current = null; setTimeout(() => root.unmount(), 0); };
	}, []);

	return <div ref={hostRef} className="dlg-embed-host" />;
}
